import { type EntityId, type SceneDocument } from '@/domain/model';
import { type DragAnchor, initialiseScene, type Scene, updateScene } from '@/engine';

import { applyActions, type EditAction } from './actions';

/**
 * Everything the editor knows.
 *
 * Immutable: every edit returns a new state. That is what makes undo a matter of keeping
 * old references (M8), what lets the React store detect changes by identity, and what
 * makes a drag preview safe to compute and throw away.
 */
export interface EditorState {
  readonly document: SceneDocument;
  readonly scene: Scene;
  readonly selection: ReadonlySet<EntityId>;
}

export interface EditResult {
  readonly state: EditorState;
  /** Entities whose evaluated value changed — what the renderer must repaint. */
  readonly changed: ReadonlySet<EntityId>;
  /**
   * Entities whose **document record** may have changed: those the actions named, those
   * that cascaded away, and the free points the constraint solver moved.
   *
   * Reported so history can diff exactly these rather than the whole document. A full
   * diff on every pointer-move of a drag would cost more than the geometry it records.
   */
  readonly touched: ReadonlySet<EntityId>;
}

export function createEditorState(document: SceneDocument): EditorState {
  const { document: solved, scene } = initialiseScene(document);
  return { document: solved, scene, selection: new Set() };
}

export interface EditOptions {
  /** Pins a dragged point towards the pointer while constraints are solved. */
  readonly anchor?: DragAnchor | undefined;
}

/**
 * Applies edits and brings the scene up to date.
 *
 * The whole pipeline in one place: actions mutate the document, then the engine runs
 * Tier 1, the constraint solver, and Tier 1 again. Callers never sequence those
 * themselves — a tool that forgot the solver pass would produce geometry that is correct
 * until the moment a constraint matters.
 */
export function applyEdit(
  state: EditorState,
  actions: readonly EditAction[],
  options: EditOptions = {}
): EditResult {
  if (actions.length === 0 && options.anchor === undefined) {
    return { state, changed: new Set(), touched: new Set() };
  }

  const applied = applyActions(state.document, actions);

  const update = updateScene(state.scene, applied.document, applied.seeds, {
    anchor: options.anchor,
  });

  const changed = new Set(update.changed);
  for (const id of applied.removed) changed.add(id);

  const touched = new Set<EntityId>(applied.seeds);
  for (const id of applied.removed) touched.add(id);
  for (const id of update.solverMoved) touched.add(id);

  // A deleted entity must not linger in the selection.
  let selection = state.selection;
  if (applied.removed.length > 0) {
    const next = new Set(selection);
    let touched = false;
    for (const id of applied.removed) {
      if (next.delete(id)) touched = true;
    }
    if (touched) selection = next;
  }

  return {
    state: { document: update.document, scene: update.scene, selection },
    changed,
    touched,
  };
}

export function withSelection(state: EditorState, ids: Iterable<EntityId>): EditorState {
  const next = new Set(ids);
  if (next.size === state.selection.size && [...next].every((id) => state.selection.has(id))) {
    return state;
  }
  return { ...state, selection: next };
}

export function toggleSelection(state: EditorState, id: EntityId): EditorState {
  const next = new Set(state.selection);
  if (!next.delete(id)) next.add(id);
  return { ...state, selection: next };
}

export function clearSelection(state: EditorState): EditorState {
  return state.selection.size === 0 ? state : { ...state, selection: new Set() };
}
